import { FileDTO, FileTypeDTO } from '@/api/trafikportalen';
import IconButton from '@/components/core/IconButton.tsx';
import { IconFileDownload } from '@/icons';
import { toast } from '@/hooks/useToast.tsx';
import { useIntl } from 'react-intl';
import { useConfig } from '@/contexts/ConfigContext';
import { useAccessToken } from '@/hooks/useAxiosAuth.ts';
import { formatDate } from '@/helpers/formatting';
import { useCallback, useMemo } from 'react';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { useFileDownload } from '@/hooks/useFileDownload';

export interface FileListItemData extends FileDTO {
  departmentNames?: string[];
  organizationName?: string;
}

interface FileListItemProps {
  file: FileListItemData;
}

export const FileListItem = ({ file }: FileListItemProps) => {
  const { formatMessage } = useIntl();
  const { baseUrl } = useConfig();
  const accessToken = useAccessToken();
  const { downloadFromUrl } = useFileDownload();

  const isSysadmin = checkUserPermissions({ permission: 'tenant:viewer' });

  const entityName = useMemo(() => {
    if (isSysadmin) {
      return file.organizationName ?? '';
    }
    return file.departmentNames?.join(', ') ?? '';
  }, [isSysadmin, file.organizationName, file.departmentNames]);

  const fileTypeLabel = useMemo(() => {
    const labels: Record<FileTypeDTO, string> = {
      'invoice-specification': formatMessage({
        defaultMessage: 'Fakturaspecifikation',
      }),
      'environmental-report': formatMessage({ defaultMessage: 'Miljörapport' }),
      'invoice-data': formatMessage({ defaultMessage: 'Fakturaunderlag' }),
    };
    return labels[file.type];
  }, [file.type, formatMessage]);

  const handleDownload = useCallback(async () => {
    try {
      await downloadFromUrl(`${baseUrl}/download/${file.id}`, file.name, {
        Authorization: `Bearer ${await accessToken()}`,
      });
    } catch (err) {
      console.error('Download failed', err);
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Nedladdningen misslyckades',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [
    baseUrl,
    file.id,
    file.name,
    accessToken,
    downloadFromUrl,
    formatMessage,
  ]);

  return (
    <div key={file.id} className="@container">
      <div className="flex size-auto flex-wrap rounded-md border border-background-300 bg-background-50 px-4 py-2">
        <div className="flex w-full flex-col p-2 @2xl:grid @2xl:grid-cols-12 @2xl:items-center @2xl:gap-2">
          <div className="flex w-full flex-row justify-between @2xl:col-span-2">
            <span>{formatDate(file.created_at)}</span>
          </div>
          <div className="flex w-full flex-col @2xl:col-span-7">
            <span className="truncate">{entityName}</span>
          </div>
          <div className="flex w-full flex-col @2xl:col-span-3">
            <span>{fileTypeLabel}</span>
            <span className="truncate text-text-700">{file.name}</span>
          </div>

          <div className="absolute right-2 top-1/2 -translate-y-1/2 items-end">
            <IconButton
              aria-label={formatMessage({ defaultMessage: 'Ladda ner fil' })}
              icon={<IconFileDownload />}
              variant="ghost"
              onClick={handleDownload}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
